import { z } from 'zod';
import { DateFromIsoString, oldCountrySchema } from '~/root/domain';
import { requestSchema } from '~/root/Requests/domain';
import { customerSchema, staffToCustomerSchemaDiff } from './user';

const alertSchema = z.object({
  key: z.string(),
  hash: z.string(),
  message: z.string(),
  type: z.enum(['info', 'success', 'warning', 'error']),
});

const popupSchema = z.object({
  key: z.enum(['upsell']),
  amount: z.string(), // currency sign + amount, f.e $30
  slides: z.number(),
  discount: z.string(), // currency sign + 50% of amount, f.e $15
  percentage: z.string(), // '50%'
});

const initCustomerSchema = customerSchema.omit({ country: true }).extend({
  country: oldCountrySchema.nullable(),
});

const initStaffSchema = initCustomerSchema.extend(staffToCustomerSchemaDiff);

const initUserSchema = initCustomerSchema.or(initStaffSchema);

const requiredConsentName = z.enum(['terms_and_conditions', 'privacy_policy', 'dpa']);
export type RequiredConsentName = z.infer<typeof requiredConsentName>;

const optionalConsentName = z.literal('news_letter');
export type OptionalConsentName = z.infer<typeof optionalConsentName>;

export type ConsentName = RequiredConsentName | OptionalConsentName;

const documentUpdatePointSchema = z.object({
  title: z.string(),
  description: z.string(),
});

export type DocumentUpdatePoint = z.infer<typeof documentUpdatePointSchema>;

const consentBaseSchema = {
  hasEverConsented: z.boolean(),
  updates: z.array(documentUpdatePointSchema),
  releaseDate: DateFromIsoString,
};

const consentSchema = z.discriminatedUnion('key', [
  z.object({
    key: z.literal('terms'),
    shouldAccept: z.array(requiredConsentName),
    ...consentBaseSchema,
  }),
  z.object({
    key: z.literal('medly'),
    ...consentBaseSchema,
  }),
]);

export type Consent = z.infer<typeof consentSchema>;
export type ConsentKey = Consent['key'];
export type TermsConsent = Extract<Consent, { key: 'terms' }>;
export type MedlyConsent = Extract<Consent, { key: 'medly' }>;

export const initSchema = z.object({
  requests: z.array(requestSchema).catch([]),
  alerts: z.array(alertSchema).nullable(),
  consents: z.array(consentSchema).nullable(),
  canUseMedly: z.boolean().catch(false),
  countryCode: z.string(),
  env: z.object({
    stripeToken: z.string(),
    userIp: z.string(),
  }),
  flashMessage: z.any().nullable(),
  isEu: z.boolean(),
  popups: z.array(popupSchema).nullable(),
  user: initUserSchema.nullable().catch(null),
  enableProCoupons: z.boolean().optional().catch(false),
});

export type Init = z.infer<typeof initSchema>;
